import { Component } from '@angular/core';
import { Wrapper } from '../../components/wrapper.component';
import { Account, JoiAccount } from 'globals/store/accounts.data';
import { budgetAccountSelector } from '../../store/reselectors/accounts.reselector';
import { AccountBudgetMonthSummary, BudgetSummary, BudgetSummaryTaxType, SimpleGridCol, SimpleGridRow } from '../../store/reselectors/budget.data';
import { Dictionary } from '@ngrx/entity';
import { accountBudgetsSummaryColumnsReselector, accountBudgetSummariesReselector } from '../../store/reselectors/budget.reselector';
import { getBeginningOfMonthDate, loadLocal, makeStringFileNameSafe, save2DStringArrayToCSVFile, saveLocal } from '../../helpers/helpers';
import { sleepForMS } from 'src/app/helpers/helpers';
import { DropdownOption } from 'globals/store/programs.data';
import { budgetActions } from 'src/app/store/budget.era';
import { DatePickerComponent } from 'src/app/components/misc/date-picker.component';
import { DropdownComponent } from 'src/app/components/misc/dropdown.component';
import { SimpleGridComponent } from './simple-grid.component';
import { ActionButtonComponent } from 'src/app/components/nav-bar/action-button.component';
import { FindByIdPipe } from 'src/app/services/pipes/findById.pipe';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'account-budget-summary',
    templateUrl: './account-budget-summary.html',
    styleUrls: ['./account-budget-summary.scss'],
    styles: [`
`],
    standalone: true,
    imports: [DatePickerComponent, FindByIdPipe, CommonModule, DropdownComponent, SimpleGridComponent, ActionButtonComponent, ],
})
export class AccountBudgetSummaryComponent extends Wrapper<AccountBudgetSummaryComponent> {
  _file = 'account-budget-summary.component'
  account: JoiAccount
  startDate
  columns: SimpleGridCol[] = []
  revenueOpen: boolean = true
  costOpen: boolean = false
  summaryOpen: boolean = false
  cashFlowOpen: boolean = false
  summaryTaxType: BudgetSummaryTaxType = 'inclusive'
  summaryTaxTypes: DropdownOption<BudgetSummaryTaxType>[] = [
    { id: 'inclusive', title: 'Tax Inclusive' },
    { id: 'exclusive', title: 'Tax exclusive' }
  ]
  revenueRows: SimpleGridRow<AccountBudgetMonthSummary>[] = [
    { key: 'completedRevenue', title: 'Completed/closed Revenue' },
    { key: 'confirmedRevenue', title: 'Confirmed Revenue' },
    { key: 'pendingRevenue', title: 'Pending Revenue' },
    { key: 'totalRevenue', title: 'Total Revenue' }
  ]
  costRows: SimpleGridRow<AccountBudgetMonthSummary>[] = [
    { key: 'completedCosts', title: 'Completed/closed Costs' },
    { key: 'confirmedCosts', title: 'Confirmed Costs' },
    { key: 'pendingCosts', title: 'Pending Costs' },
    { key: 'totalCosts', title: 'Total Costs' }
  ]
  summaryRows: SimpleGridRow<AccountBudgetMonthSummary>[] = [
    { key: 'totalRevenue', title: 'Revenue Total' },
    { key: 'totalCosts', title: 'Cost Total' },
    { key: 'profit', title: 'Margin' },
    { key: 'profitMargin', title: 'Margin %' },
  ]
  cashFlowRows: SimpleGridRow<AccountBudgetMonthSummary>[] = [
    { key: 'revenuePaid', title: 'Total Revenue Paid' },
    { key: 'revenuePending', title: 'Total Revenue Pending' },
    { key: 'billsPaid', title: 'Total Bills Paid' },
    { key: 'billsPending', title: 'Total Bills Pending' },
    { key: 'cashFlow', title: 'Cash flow for the month' },
  ]

  readOnly = false
  monthlySummaries: Dictionary<AccountBudgetMonthSummary>

  constructor() {
    super()
    this.selectCallback<Account, any>(budgetAccountSelector, null, account => {
      this.account = account
      this.checkDate()
    })

    this.subProp(this.store.select('budget', 'summaryStartDate'), 'startDate')
    this.selectProp(accountBudgetSummariesReselector, null, 'monthlySummaries')
    this.selectProp(accountBudgetsSummaryColumnsReselector, null, 'columns')


    this.summaryTaxType = loadLocal<BudgetSummaryTaxType>('budgetSummaryTaxType', 'inclusive')
    this.store.dispatch(budgetActions.updateSummaryTaxType(this.summaryTaxType))
  }

  async checkDate() {
    if (!this.account) return
    await sleepForMS(500)
    const defaultDate = getBeginningOfMonthDate(new Date())
    const date = loadLocal(`budgetSummaryStartDate:${this.account.id}`, defaultDate)
    this.changeDate(getBeginningOfMonthDate(date)) // double sure
    /*
      if (date) this.changeDate(new Date(date))
      else {
        const defaultDate =  new Date(this.account.created.date.seconds * 1000) // default date is now the beginning of this month, not account creation date...
        this.changeDate(defaultDate) 
      }
    */
  }

  updateSummaryTaxType(taxType: DropdownOption<BudgetSummaryTaxType>) {
    this.summaryTaxType = taxType.id
    saveLocal('budgetSummaryTaxType', taxType.id)
    this.store.dispatch(budgetActions.updateSummaryTaxType(this.summaryTaxType))
  }


  changeDate(date: Date) {
    this.startDate = date
    saveLocal(`budgetSummaryStartDate:${this.account.id}` , date.getTime())
    this.store.dispatch(budgetActions.updateSummaryStartDate(date))
  }

  private rowsToStrings(rows: SimpleGridRow<AccountBudgetMonthSummary>[]): string[][] {
    return rows.map(r => [ r.title, 
      ...this.columns.map(c => this.monthlySummaries[c.key])
                     .map(s => !s ? '' : `${s[r.key] || ''}`) 
    ])
  }

  export() {

    const header = [ '', ...this.columns.map(c => c.title)]
    const fileName = `${this.columns[0].title} - ${this.columns[this.columns.length - 2].title} Budget (${this.account.title || 'Unnamed Account'})`

    const table: string[][] = [ header,
      ...this.rowsToStrings(this.revenueRows),
      ...this.rowsToStrings(this.costRows),
      ...this.rowsToStrings(this.summaryRows),
      ...this.rowsToStrings(this.cashFlowRows)
    ]

    save2DStringArrayToCSVFile(table, `${makeStringFileNameSafe(fileName)}.csv`)
  }

  toggleRev() {
    console.log({ revenueOpen: this.revenueOpen })
  }

}

